import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import FormApi from "../../Api/FormApi";
import { FormDto } from "../../Models/FormDto";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import { toast } from "react-toastify";
import { Button, Modal } from "flowbite-react";

const AdminFormsListPage: React.FC = () => {
  const [forms, setForms] = useState<FormDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<FormDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      // Redirect to home or unauthorized page
      navigate("/"); // or another route like "/unauthorized"
    }
  }, [userModel, navigate]);

  useEffect(() => {
    FormApi.getAllForms().then((res) => {
      setForms(res.data);
      setLoading(false);
    });
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget?.formId) return;
    try {
      setIsDeleting(true);
      await FormApi.deleteForm(deleteTarget.formId);
      setForms((prev) => prev.filter((f) => f.formId !== deleteTarget.formId));
      toast.success("Form archived.");
      setDeleteTarget(null);
    } catch (err) {
      toast.error("Unable to archive form.");
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="p-4 w-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Manage Forms</h1>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded"
          onClick={() => navigate("/forms/new")}
        >
          + New Form
        </button>
      </div>

      <div className="overflow-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="text-left px-4 py-2">ID</th>
              <th className="text-left px-4 py-2">Title</th>
              <th className="text-left px-4 py-2">Active</th>
              <th className="text-left px-4 py-2">Start</th>
              <th className="text-left px-4 py-2">End</th>
              <th className="text-left px-4 py-2">Public Link</th>
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {forms.map((form) => (
              <tr key={form.formId} className="border-t">
                <td className="px-4 py-2 font-mono text-xs">{form.formId}</td>
                <td className="px-4 py-2">{form.title}</td>
                <td className="px-4 py-2">{form.isActive ? "Yes" : "No"}</td>
                <td className="px-4 py-2">
                  {form.startDate?.substring(0, 10)}
                </td>
                <td className="px-4 py-2">{form.endDate?.substring(0, 10)}</td>
                <td className="px-4 py-2">
                  <a
                    href={`/submitform/${form.formId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Open
                  </a>
                </td>
                <td className="px-4 py-2 space-x-2">
                  <Link
                    to={`/forms/manage/${form.formId}`}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => navigate(`/forms/new?copyFrom=${form.formId}`)}
                    className="text-indigo-600 hover:underline"
                  >
                    Copy
                  </button>
                  <Link
                    to={`/forms/${form.formId}/responses`}
                    className="text-green-600 hover:underline"
                  >
                    Responses
                  </Link>
                  <button
                    onClick={() => setDeleteTarget(form)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        show={!!deleteTarget}
        onClose={() => (isDeleting ? null : setDeleteTarget(null))}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Archive form?</h3>
            <p className="text-sm text-gray-600">
              This will archive <span className="font-medium">{deleteTarget?.title ?? "this form"}</span>.
              It will no longer appear in active form lists, but responses/history are kept.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                color="gray"
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button color="failure" onClick={handleDelete} isProcessing={isDeleting}>
                {isDeleting ? "Archiving..." : "Archive"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminFormsListPage;
